import { editorUrl } from '@/lib/editorBasePath';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { toast } from '@/hooks/use-toast';
import { ImageIcon, Loader2, Heart, ChevronDown, ChevronRight, Plus, Trash2, Check } from 'lucide-react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { TrameBackgroundInfosByStyle } from '@/hooks/useBookLoader';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ApiBgItem {
  id: number;
  image_url: string;
  is_favoris?: boolean;
}

/** A category entry can be a single item or a group of items (displayed side by side) */
type ApiBgEntry = ApiBgItem | ApiBgItem[];

type ApiBgResponse = Record<string, ApiBgEntry[]>;

interface BackgroundPanelProps {
  onSetBackground: (type: 'color' | 'image', value: string, backgroundTemplateId?: number, isCustom?: boolean) => void;
  activeBackgroundId?: number | null;
  activeBackgroundCustomId?: number | null;
  trameBackgroundInfos: TrameBackgroundInfosByStyle;
  favColors: string[];
  onFavColorsChange: (colors: string[]) => void;
  isWememberz?: boolean;
}

function getCsrfToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') ?? '';
}

const BackgroundPanel: React.FC<BackgroundPanelProps> = ({ onSetBackground, activeBackgroundId, activeBackgroundCustomId, trameBackgroundInfos, favColors, onFavColorsChange, isWememberz = false }) => {
  const customBgInputRef = useRef<HTMLInputElement>(null);
  const [backgrounds, setBackgrounds] = useState<ApiBgResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  // expandedCategories: only keys that have been explicitly opened
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [loadingCategories, setLoadingCategories] = useState<Set<string>>(new Set());
  const [loadedCategories, setLoadedCategories] = useState<Set<string>>(new Set());
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customBgs, setCustomBgs] = useState<ApiBgItem[]>([]);
  const [customBgsLoading, setCustomBgsLoading] = useState(true);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [pendingColor, setPendingColor] = useState('#000000');
  const [favoriBgs, setFavoriBgs] = useState<ApiBgItem[]>([]);
  const [favoriBgsLoading, setFavoriBgsLoading] = useState(true);
  const [customBgToDelete, setCustomBgToDelete] = useState<ApiBgItem | null>(null);
  const [deletingCustomBg, setDeletingCustomBg] = useState(false);

  const handleDeleteCustomBg = async () => {
    if (!customBgToDelete) return;
    const bg = customBgToDelete;
    setDeletingCustomBg(true);
    try {
      const res = await fetch(editorUrl(`/remove_custom_trame_background?id=${bg.id}`), {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      const data = await res.json().catch(() => ({} as any));
      if (data?.result === true) {
        setCustomBgs((prev) => prev.filter((b) => b.id !== bg.id));
      } else {
        toast({
          title: 'Suppression impossible',
          description: data?.message || 'Une erreur est survenue.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.warn('[BackgroundPanel] Delete custom bg failed:', err);
      toast({ title: 'Suppression impossible', description: 'Une erreur réseau est survenue.', variant: 'destructive' });
    } finally {
      setDeletingCustomBg(false);
      setCustomBgToDelete(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetch(editorUrl('/load_trame_backgrounds_custom'))
      .then((res) => res.json())
      .then((data: ApiBgItem[]) => {
        if (!cancelled) setCustomBgs(Array.isArray(data) ? data : []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setCustomBgsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Load favorites backgrounds via dedicated endpoint
  useEffect(() => {
    let cancelled = false;
    fetch(editorUrl('/load_trame_backgrounds_for_category?category=favoris'))
      .then((res) => res.json())
      .then((data: ApiBgItem[]) => {
        if (!cancelled) {
          setFavoriBgs(Array.isArray(data) ? data : []);
          // Sync favorites set from loaded data
          const favIds = new Set<number>((Array.isArray(data) ? data : []).map((item) => item.id));
          setFavorites((prev) => {
            const next = new Set(prev);
            favIds.forEach((id) => next.add(id));
            return next;
          });
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setFavoriBgsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(editorUrl('/load_trame_backgrounds'))
      .then((res) => res.json())
      .then((data: ApiBgResponse) => {
        if (!cancelled) {
          setBackgrounds(data);
          const favSet = new Set<number>();
          for (const entries of Object.values(data)) {
            for (const entry of entries) {
              const items = Array.isArray(entry) ? entry : [entry];
              for (const item of items) {
                if (item.is_favoris) favSet.add(item.id);
              }
            }
          }
          setFavorites(favSet);
          // All categories start collapsed (expandedCategories stays empty)
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const toggleFavorite = useCallback((e: React.MouseEvent, bgId: number) => {
    e.stopPropagation();
    const isFav = favorites.has(bgId);
    const endpoint = isFav ? '/editor/remove_background_to_favori' : '/editor/add_background_to_favori';

    setFavorites((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(bgId);
      else next.add(bgId);
      return next;
    });

    // Sync favoriBgs list optimistically
    if (isFav) {
      setFavoriBgs((prev) => prev.filter((item) => item.id !== bgId));
    } else {
      if (backgrounds) {
        let found: ApiBgItem | undefined;
        for (const entries of Object.values(backgrounds)) {
          if (!Array.isArray(entries)) continue;
          for (const entry of entries as Iterable<unknown>) {
            const items = Array.isArray(entry) ? entry : [entry];
            found = items.find((item) => item.id === bgId);
            if (found) break;
          }
          if (found) break;
        }
        if (found) {
          setFavoriBgs((prev) => [...prev, found!]);
        }
      }
    }

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ background_id: bgId }),
    }).catch(() => {
      setFavorites((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(bgId);
        else next.delete(bgId);
        return next;
      });
      // Re-fetch favorites on error
      fetch(editorUrl('/load_trame_backgrounds_for_category?category=favoris'))
        .then((res) => res.json())
        .then((data: ApiBgItem[]) => setFavoriBgs(Array.isArray(data) ? data : []))
        .catch(() => {});
    });
  }, [favorites, backgrounds]);

  const toggleCategory = (key: string) => {
    const isCurrentlyExpanded = expandedCategories.has(key);

    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

    // If opening and not yet loaded, fetch backgrounds for this category
    if (!isCurrentlyExpanded && !loadedCategories.has(key)) {
      setLoadingCategories((prev) => new Set(prev).add(key));
      fetch(editorUrl(`/load_trame_backgrounds_for_category?category=${encodeURIComponent(key)}`))
        .then((res) => res.json())
        .then((data: ApiBgEntry[]) => {
          setBackgrounds((prev) => {
            if (!prev) return prev;
            return { ...prev, [key]: Array.isArray(data) ? data : [] };
          });
          setLoadedCategories((prev) => new Set(prev).add(key));
        })
        .catch(() => {})
        .finally(() => {
          setLoadingCategories((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        });
    }
  };


  const [uploadProgress, setUploadProgress] = useState<{ done: number; total: number } | null>(null);

  const handleCustomBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let lastResult: { image_url: string; id: number } | null = null;
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await fetch(editorUrl('/add_custom_trame_background'), {
            method: 'POST',
            headers: { 'X-CSRF-Token': getCsrfToken() },
            body: formData,
          });
          if (!res.ok) throw new Error('Upload failed');
          const data = await res.json();
          if (data?.image_url) {
            setCustomBgs((prev) => [...prev, { id: data.id, image_url: data.image_url }]);
            lastResult = { image_url: data.image_url, id: data.id };
          }
        } catch (err) {
          console.warn('[BackgroundPanel] Custom upload failed:', err);
        }
        setUploadProgress({ done: i + 1, total: files.length });
      }
      if (lastResult) {
        onSetBackground('image', lastResult.image_url, lastResult.id, true);
      }
      // Refresh custom backgrounds from server
      try {
        const res = await fetch(editorUrl('/load_trame_backgrounds_custom'));
        const data = await res.json();
        if (Array.isArray(data)) setCustomBgs(data);
      } catch {}
    } finally {
      setUploading(false);
      setUploadProgress(null);
      setShowCustomDialog(false);
      e.target.value = '';
    }
  };


  const handleAddColor = () => {
    const color = pendingColor;
    onFavColorsChange([...favColors, color]);
    setColorPickerOpen(false);
    setPendingColor('#000000');
    fetch(editorUrl('/save_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_color: color }),
    }).catch(() => {
      onFavColorsChange(favColors.filter((c) => c !== color));
    });
  };

  const handleRemoveColor = (color: string) => {
    onFavColorsChange(favColors.filter((c) => c !== color));
    fetch(editorUrl('/remove_favorites_colors'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ color }),
    }).catch(() => {
      onFavColorsChange([...favColors, color]);
    });
  };

  const sortedKeys = backgrounds ? Object.keys(backgrounds).sort((a, b) => Number(a) - Number(b)) : [];
  const visibleKeys = sortedKeys;

  const renderBgItem = (item: ApiBgItem, category: string, isCustom = false) => (
    <div key={item.id} className="relative group">
      <button
        onClick={() => onSetBackground('image', item.image_url, item.id, isCustom)}
        className={`w-full rounded-md border overflow-hidden transition-colors ${
          (isCustom ? activeBackgroundCustomId : activeBackgroundId) === item.id
            ? 'border-editor-sidebar-active ring-1 ring-editor-sidebar-active'
            : 'border-editor-sidebar-border hover:border-editor-sidebar-active'
        }`}
      >
        <img
          src={item.image_url}
          alt={`Fond ${category}`}
          className="w-full aspect-[3/4] object-cover"
          loading="lazy"
        />
      </button>
      {!isCustom && (
        <button
          onClick={(e) => toggleFavorite(e, item.id)}
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          title={favorites.has(item.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          <Heart
            size={12}
            className={favorites.has(item.id) ? 'text-red-500 fill-red-500' : 'text-white'}
          />
        </button>
      )}
      {isCustom && (
        <button
          onClick={(e) => { e.stopPropagation(); setCustomBgToDelete(item); }}
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/40 hover:bg-black/60 transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          title="Supprimer ce fond"
        >
          <Trash2 size={12} className="text-white" />
        </button>
      )}
    </div>
  );

  /** Render a single entry which can be one item or a group of items displayed side by side */
  const renderBgEntry = (entry: ApiBgEntry, category: string, isCustom = false) => {
    if (Array.isArray(entry)) {
      // Group of items – display side by side
      return (
        <div key={entry.map((e) => e.id).join('-')} className="col-span-2 grid gap-2" style={{ gridTemplateColumns: `repeat(${entry.length}, 1fr)` }}>
          {entry.map((item) => renderBgItem(item, category, isCustom))}
        </div>
      );
    }
    return renderBgItem(entry, category, isCustom);
  };

  return (
    <div className="space-y-4">
      {/* Import button at top */}
      <button
        onClick={() => setShowCustomDialog(true)}
        className="w-full py-2 px-3 rounded-md text-sm bg-primary-gradient text-primary-foreground transition-colors flex items-center justify-center gap-2"
      >
        Importer mes propres fonds ou mes pages perso
      </button>

      {/* Custom upload dialog */}
      <Dialog open={showCustomDialog} onOpenChange={setShowCustomDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajout d'un ou plusieurs fonds de page</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              *Les images/PDF doivent faire 220x307mm (soit un A4 + 10mm de marge) en résolution 300dpi. Les marges seront coupées à l'impression
            </p>
            <input
              ref={customBgInputRef}
              type="file"
              accept="image/*,application/pdf"
              multiple
              className="hidden"
              onChange={handleCustomBgUpload}
            />
            {uploading ? (
              <div className="flex flex-col items-center justify-center py-6 gap-2">
                <Loader2 className="animate-spin text-muted-foreground" size={28} />
                {uploadProgress && (
                  <p className="text-xs text-muted-foreground">
                    Import en cours… {uploadProgress.done}/{uploadProgress.total}
                  </p>
                )}
              </div>
            ) : (
              <button
                onClick={() => customBgInputRef.current?.click()}
                className="w-full py-3 px-4 rounded-md text-sm bg-primary-gradient text-primary-foreground transition-colors flex items-center justify-center gap-2 border border-dashed border-editor-sidebar-border"
              >
                <ImageIcon size={18} />
                Choisir une ou plusieurs images
              </button>
            )}

          </div>
        </DialogContent>
      </Dialog>

      {/* Couleurs */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center">
          Couleur de fond
        </h3>
        <div className="grid grid-cols-6 gap-1.5">
          {favColors.map((color, idx) => (
            <div key={`${color}-${idx}`} className="relative group aspect-square">
              <button
                onClick={() => onSetBackground('color', color)}
                className="w-full h-full rounded-md border border-editor-sidebar-border hover:scale-110 transition-transform"
                style={{ backgroundColor: color }}
              />
              <button
                onClick={() => handleRemoveColor(color)}
                className="absolute top-0 right-0 p-0.5 rounded-bl-md rounded-tr-md bg-black/60 hover:bg-black/80 transition-colors opacity-0 group-hover:opacity-100"
                title="Supprimer cette couleur"
              >
                <Trash2 size={10} className="text-white" />
              </button>
            </div>
          ))}
          <div className="aspect-square">
            <button
              onClick={() => setColorPickerOpen(true)}
              className="w-full h-full rounded-md border border-dashed border-editor-sidebar-border hover:border-editor-sidebar-active transition-colors flex items-center justify-center"
              title="Ajouter une couleur"
            >
              <Plus size={12} className="text-muted-foreground" />
            </button>
          </div>
        </div>

        <Dialog open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
          <DialogContent className="sm:max-w-sm p-0 gap-0 overflow-hidden">
            <DialogTitle className="px-5 pt-5 pb-0 text-sm font-semibold">Choisir une couleur</DialogTitle>
            <div className="p-5 space-y-4">
              <HexColorPicker color={pendingColor} onChange={setPendingColor} style={{ width: '100%', height: 180 }} />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg border border-border shrink-0 shadow-sm" style={{ backgroundColor: pendingColor }} />
                <HexColorInput
                  color={pendingColor}
                  onChange={setPendingColor}
                  prefixed
                  className="flex-1 text-sm font-mono bg-muted text-foreground rounded-md px-3 py-2 border border-border uppercase"
                />
                <button
                  onClick={handleAddColor}
                  className="flex items-center gap-1.5 text-sm bg-primary-gradient text-primary-foreground px-4 py-2 rounded-md hover:opacity-90 transition-opacity shrink-0 font-medium"
                >
                  <Check size={14} />
                  Ajouter
                </button>
              </div>
            </div>
            {favColors.length > 0 && (
              <div className="border-t border-border px-5 py-4 bg-muted/30">
                <p className="text-xs text-muted-foreground mb-2">Mes couleurs enregistrées</p>
                <div className="flex flex-wrap gap-2">
                  {favColors.map((color, idx) => (
                    <div key={`modal-${color}-${idx}`} className="relative group">
                      <button
                        onClick={() => setPendingColor(color)}
                        className="w-8 h-8 rounded-lg border border-border hover:scale-110 transition-transform shadow-sm"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                      <button
                        onClick={() => handleRemoveColor(color)}
                        className="absolute -top-1 -right-1 p-0.5 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Fonds depuis l'API */}
      <div className="space-y-3">
        <h3 className="text-sm font-medium text-editor-sidebar-foreground text-center">
          Fonds de page
        </h3>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="animate-spin text-muted-foreground" size={20} />
          </div>
        )}

        {!loading && (!backgrounds || visibleKeys.length === 0) && (
          <p className="text-xs text-muted-foreground">Aucun fond disponible.</p>
        )}

        {!loading && backgrounds && visibleKeys.length > 0 && (
          <>
            {/* Category filter chips */}
            <div className="flex flex-wrap gap-1.5">
              {/* "Mes fonds" and "Favoris" are always open — shown as active */}
              <span
                className="text-[10px] px-2 py-1 rounded-full border transition-colors bg-editor-sidebar-active text-white border-editor-sidebar-active cursor-default"
              >
                📁 Mes fonds
              </span>
              <span
                className="text-[10px] px-2 py-1 rounded-full border transition-colors bg-editor-sidebar-active text-white border-editor-sidebar-active cursor-default"
              >
                ❤️ Favoris
              </span>
              {visibleKeys.map((key) => {
                const info = trameBackgroundInfos[key];
                const isExpanded = expandedCategories.has(key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleCategory(key)}
                    className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
                      isExpanded
                        ? 'bg-editor-sidebar-active text-white border-editor-sidebar-active'
                        : 'bg-transparent text-editor-sidebar-foreground border-editor-sidebar-border hover:border-editor-sidebar-active'
                    }`}
                  >
                    {info?.name || key}
                  </button>
                );
              })}
            </div>

            {/* Mes fonds section – always open */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">📁 Mes fonds</h4>
              {customBgsLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="animate-spin text-muted-foreground" size={16} />
                </div>
              ) : customBgs.length === 0 ? (
                <p className="text-[10px] text-muted-foreground italic">Aucun fond personnalisé.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {customBgs.map((item) => renderBgItem(item, 'mes fonds', true))}
                </div>
              )}
            </div>

            {/* Favoris section – always open, loaded from API */}
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">❤️ Favoris</h4>
              {favoriBgsLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Loader2 className="animate-spin text-muted-foreground" size={16} />
                </div>
              ) : favoriBgs.length === 0 ? (
                <p className="text-[10px] text-muted-foreground italic">Aucun favori pour le moment.</p>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {favoriBgs.map((item) => renderBgItem(item, 'favoris'))}
                </div>
              )}
            </div>

            {/* Category sections – collapsed by default, expand on click */}
            {visibleKeys.map((key) => {
              const items = Array.isArray(backgrounds[key]) ? backgrounds[key] : [];
              // Hide only if loaded and empty
              if (items.length === 0 && loadedCategories.has(key)) return null;
              const info = trameBackgroundInfos[key];
              const categoryName = info?.name || key;
              const isExpanded = expandedCategories.has(key);

              return (
                <div key={key} className="space-y-2">
                  <button
                    onClick={() => toggleCategory(key)}
                    className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-editor-sidebar-foreground transition-colors w-full"
                  >
                    {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    <span className="capitalize">{categoryName}</span>
                  </button>
                  {isExpanded && (
                    loadingCategories.has(key) ? (
                      <div className="flex items-center justify-center py-3">
                        <Loader2 className="animate-spin text-muted-foreground" size={16} />
                      </div>
                    ) : items.length === 0 ? (
                      <p className="text-[10px] text-muted-foreground italic">Aucun fond disponible.</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        {items.map((entry) => renderBgEntry(entry, categoryName))}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>


      <AlertDialog open={!!customBgToDelete} onOpenChange={(open) => { if (!open) setCustomBgToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce fond ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est définitive. Le fond sera retiré de vos fonds personnalisés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingCustomBg}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteCustomBg(); }}
              disabled={deletingCustomBg}
            >
              {deletingCustomBg ? <Loader2 className="animate-spin" size={14} /> : null}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BackgroundPanel;
